import { canvasState } from "@design-lab/features/canvas";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { trackZoomClicked } from "@design-lab/shared/lib/analytics";
import { Button } from "@design-lab/shared/ui";
import { useAtom } from "jotai";
import { Minus, Plus, RotateCcw, ZoomIn, ZoomOut } from "lucide-react";

type RefineZoomProps = {
  maxZoom?: number;
};

const ZOOM_STEP = 0.25;
const MIN_ZOOM = 1;
const RESET_ZOOM = 2; // 200%

export function RefineZoom({ maxZoom = 10 }: RefineZoomProps) {
  const { isDesktop } = useResponsive();
  const [zoom, setZoom] = useAtom(canvasState.zoomAtom);
  const [pan, setPan] = useAtom(canvasState.panAtom);
  const zoomPercentage = Math.round(zoom * 100);

  const handleZoomIn = () => {
    const newZoom = Math.min(zoom + ZOOM_STEP, maxZoom);
    setZoom(newZoom);
    trackZoomClicked(newZoom);
  };

  const handleZoomOut = () => {
    const newZoom = Math.max(zoom - ZOOM_STEP, MIN_ZOOM);
    setZoom(newZoom);
    trackZoomClicked(newZoom);
  };

  const handleReset = () => {
    setZoom(RESET_ZOOM);
    setPan({ x: 0, y: 0 });
    trackZoomClicked(RESET_ZOOM);
  };

  const canZoomIn = zoom < maxZoom;
  const canZoomOut = zoom > MIN_ZOOM;
  const canReset = zoom !== RESET_ZOOM || pan.x !== 0 || pan.y !== 0;

  if (!isDesktop) {
    return (
      <div className="flex h-10 w-20 items-center rounded-lg bg-white shadow-md">
        <button
          data-testid="refine-zoom-out-button"
          className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-l-lg p-1.5 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={handleZoomOut}
          disabled={!canZoomOut}
        >
          <ZoomOut className="h-5 w-5" />
        </button>
        <div className="h-8 w-px bg-gray-100"></div>
        <button
          data-testid="refine-zoom-in-button"
          className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-r-lg p-1.5 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={handleZoomIn}
          disabled={!canZoomIn}
        >
          <ZoomIn className="h-5 w-5" />
        </button>
      </div>
    );
  }

  return (
    <div className="bg-background flex w-auto items-center rounded-md border lg:h-14 lg:w-fit lg:gap-2 lg:p-1">
      <Button
        data-testid="refine-zoom-reset-button"
        variant="icon"
        className="rounded-none border-r p-1 lg:h-full lg:w-10 lg:border-none"
        onPress={handleReset}
        isDisabled={!canReset}
        aria-label="Reset zoom to 200% and center view"
      >
        <RotateCcw className="size-4" />
      </Button>

      {/* Separator */}
      <div className="w-full border-t lg:block lg:h-full lg:w-px lg:border-l lg:border-t-0" />

      <Button
        data-testid="refine-zoom-out-button"
        variant="icon"
        className="rounded-none border-r p-1 lg:h-full lg:w-10 lg:rounded-md lg:border-none lg:p-0"
        onPress={handleZoomOut}
        isDisabled={!canZoomOut}
        aria-label="Zoom out"
      >
        <Minus className="size-4" />
      </Button>

      <div
        role="status"
        aria-label="Zoom percentage"
        className="bg-background flex select-none items-center justify-center px-1 py-2 text-sm font-medium text-gray-700 lg:h-full lg:w-16 lg:rounded-md lg:border lg:px-4"
      >
        {zoomPercentage}%
      </div>

      <Button
        data-testid="refine-zoom-in-button"
        variant="icon"
        className="rounded-l-none rounded-r-md border-l p-1 lg:h-full lg:w-10 lg:rounded-md lg:border-none lg:p-0"
        onPress={handleZoomIn}
        isDisabled={!canZoomIn}
        aria-label="Zoom in"
      >
        <Plus className="size-4" />
      </Button>
    </div>
  );
}
